import type { OssArchiveRestoreStatus } from "./oss-archive-error";
import {
	createDefaultOssArchiveLocales,
	createDefaultLocaleMessages,
	mergeOssArchiveLocales,
	OSS_ARCHIVE_DEFAULT_WAIT_TEXT,
	type OssArchiveDialogCopyItem,
	type OssArchiveLocaleMessages,
	type OssArchiveLocales,
	type OssArchiveToastCopy
} from "./oss-archive-copy-defaults";
import {
	normalizeLocaleCode,
	pickOssArchiveLocaleMessages,
	resolveOssArchiveLocale
} from "./oss-archive-locale";

export type { OssArchiveDialogCopyItem, OssArchiveLocaleMessages, OssArchiveLocales, OssArchiveToastCopy };

export type OssArchiveDialogVersion = "A" | "B" | "C" | "D";

export type OssArchiveDialogState =
	| "need"
	| "pending"
	| "ready"
	| "expired"
	| "failed"
	| "forbidden"
	| "degrade";

export interface OssArchiveUiCopy {
	estimatedWaitText?: string;
	archiveAgeYearsText?: string;
	openApiDegradeMessage?: string;
	dialogVersion?: OssArchiveDialogVersion | string;
	defaultLocale?: string;
	locales?: OssArchiveLocales;
}

export interface OssArchiveDialogCopy {
	title: string;
	body: string;
	primary: string;
	secondary: string;
}

const ZH_DIALOG = createDefaultOssArchiveLocales()["zh-CN"];

const VERSION_COPY: Record<
	OssArchiveDialogVersion,
	Record<OssArchiveDialogState, OssArchiveDialogCopy>
> = {
	A: {
		need: ZH_DIALOG.need,
		pending: ZH_DIALOG.pending,
		ready: ZH_DIALOG.ready,
		expired: ZH_DIALOG.expired,
		failed: ZH_DIALOG.failed,
		forbidden: ZH_DIALOG.forbidden,
		degrade: ZH_DIALOG.degrade
	},
	B: {} as Record<OssArchiveDialogState, OssArchiveDialogCopy>,
	C: {} as Record<OssArchiveDialogState, OssArchiveDialogCopy>,
	D: {} as Record<OssArchiveDialogState, OssArchiveDialogCopy>
};

// 保留 B/C/D 兜底（与历史默认一致，未配 Redis 多语言时使用）
Object.assign(VERSION_COPY, {
	B: {
		need: {
			title: "数据已转入归档存储",
			body: "根据国家数据安全保护相关法律法规及平台数据留存策略，你访问的数据已转入归档存储。访问前需完成一次恢复流程。",
			primary: "申请恢复访问",
			secondary: "取消"
		},
		pending: {
			title: "恢复申请办理中",
			body: "恢复申请已提交，正在办理。请稍后再次点击预览，无需重复申请。",
			primary: "",
			secondary: "关闭"
		},
		ready: {
			title: "已可重新获取",
			body: "恢复已完成，可以预览或下载。",
			primary: "立即预览",
			secondary: "关闭"
		},
		expired: {
			title: "恢复访问已过期",
			body: "恢复访问时限已结束，如需再次访问，请重新申请。",
			primary: "再次申请",
			secondary: "关闭"
		},
		failed: {
			title: "恢复申请未完成",
			body: "恢复申请未完成，请重试。",
			primary: "重试",
			secondary: "关闭"
		},
		forbidden: {
			title: "数据已转入归档存储",
			body: "当前账号无权申请恢复访问，请联系管理员。",
			primary: "关闭",
			secondary: ""
		},
		degrade: {
			title: "数据已转入归档存储",
			body: "你访问的数据已转入归档存储。当前页面无法申请恢复访问，请前往门店后台办理。",
			primary: "",
			secondary: "关闭"
		}
	}
});
VERSION_COPY.C = { ...VERSION_COPY.A };
VERSION_COPY.D = { ...VERSION_COPY.B };

export function resolveDialogVersion(version?: string): OssArchiveDialogVersion {
	if (version === "B" || version === "C" || version === "D") {
		return version;
	}
	return "A";
}

export function applyUiCopyPlaceholders(
	text: string,
	uiCopy?: OssArchiveUiCopy,
	locale?: string
): string {
	const resolved = normalizeLocaleCode(locale || resolveOssArchiveLocale());
	const waitDefault = OSS_ARCHIVE_DEFAULT_WAIT_TEXT[resolved] || OSS_ARCHIVE_DEFAULT_WAIT_TEXT["zh-CN"];
	const waitText =
		resolved === "zh-CN" && uiCopy?.estimatedWaitText ? uiCopy.estimatedWaitText : waitDefault;
	const ageText = uiCopy?.archiveAgeYearsText || "N";
	return text.replace(/\bT\b/g, waitText).replace(/\bN\b/g, ageText);
}

export function getMergedOssArchiveLocales(uiCopy?: OssArchiveUiCopy): OssArchiveLocales {
	return mergeOssArchiveLocales(uiCopy?.locales, createDefaultOssArchiveLocales());
}

export function getOssArchiveLocaleMessages(
	uiCopy?: OssArchiveUiCopy,
	locale?: string
): OssArchiveLocaleMessages {
	const resolved = normalizeLocaleCode(locale || resolveOssArchiveLocale());
	const merged = getMergedOssArchiveLocales(uiCopy);
	const picked =
		pickOssArchiveLocaleMessages(merged, resolved, uiCopy?.defaultLocale || "zh-CN") ||
		createDefaultLocaleMessages(resolved);
	return picked;
}

export function getOssArchiveDialogCopy(
	state: OssArchiveDialogState,
	uiCopy?: OssArchiveUiCopy,
	locale?: string
): OssArchiveDialogCopy {
	const messages = getOssArchiveLocaleMessages(uiCopy, locale);
	const fromRedis = messages[state];
	if (fromRedis?.title) {
		return {
			title: fromRedis.title,
			body: applyUiCopyPlaceholders(fromRedis.body, uiCopy, locale),
			primary: fromRedis.primary,
			secondary: fromRedis.secondary
		};
	}
	const version = resolveDialogVersion(uiCopy?.dialogVersion);
	const raw = VERSION_COPY[version]?.[state] || VERSION_COPY.A[state];
	return {
		title: raw.title,
		body: applyUiCopyPlaceholders(raw.body, uiCopy, locale),
		primary: raw.primary,
		secondary: raw.secondary
	};
}

export function getOssArchiveHelpWhyBody(uiCopy?: OssArchiveUiCopy, locale?: string): string {
	const messages = getOssArchiveLocaleMessages(uiCopy, locale);
	return applyUiCopyPlaceholders(messages.helpWhyBody, uiCopy, locale);
}

export function getOssArchiveHelpWhyTitle(uiCopy?: OssArchiveUiCopy, locale?: string): string {
	return getOssArchiveLocaleMessages(uiCopy, locale).helpWhyTitle;
}

export function getOssArchiveWhyLinkText(uiCopy?: OssArchiveUiCopy, locale?: string): string {
	return getOssArchiveLocaleMessages(uiCopy, locale).whyLinkText;
}

export function getOssArchiveToastCopy(
	uiCopy?: OssArchiveUiCopy,
	locale?: string
): OssArchiveToastCopy {
	return getOssArchiveLocaleMessages(uiCopy, locale).toasts;
}

export function getOssArchiveCloseText(uiCopy?: OssArchiveUiCopy, locale?: string): string {
	return getOssArchiveLocaleMessages(uiCopy, locale).closeText || "关闭";
}

export function getOssArchivePreviewHint(uiCopy?: OssArchiveUiCopy, locale?: string): string {
	const messages = getOssArchiveLocaleMessages(uiCopy, locale);
	return messages.previewHint || getOssArchiveDialogCopy("need", uiCopy, locale).title;
}

export function getOssArchiveDegradeCopy(
	uiCopy?: OssArchiveUiCopy,
	locale?: string
): OssArchiveDialogCopy {
	if (uiCopy?.openApiDegradeMessage) {
		return {
			title: getOssArchiveDialogCopy("degrade", uiCopy, locale).title,
			body: applyUiCopyPlaceholders(uiCopy.openApiDegradeMessage, uiCopy, locale),
			primary: "",
			secondary:
				getOssArchiveDialogCopy("degrade", uiCopy, locale).secondary ||
				getOssArchiveLocaleMessages(uiCopy, locale).closeText
		};
	}
	return getOssArchiveDialogCopy("degrade", uiCopy, locale);
}

export function resolveDialogStateFromRestoreStatus(
	restoreStatus?: OssArchiveRestoreStatus | string
): OssArchiveDialogState {
	switch (restoreStatus) {
		case "pending":
		case "restoring":
			return "pending";
		case "ready":
			return "ready";
		case "expired":
			return "expired";
		case "failed":
			return "failed";
		default:
			return "need";
	}
}

export { createDefaultOssArchiveLocales, mergeOssArchiveLocales, resolveOssArchiveLocale };
